// create-nfa-card.tsx

import React, { useContext, useEffect, useRef, useState } from 'react';
import { Editor } from 'primereact/editor';
import DOMPurify from 'dompurify';
import 'quill/dist/quill.snow.css';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { ToastContext } from 'app/context/ToastContext';
import { ErrorMessage, Form, Formik } from 'formik';
import { InputText } from 'primereact/inputtext';
import { createCompetition, getCompetition, updateCompetition } from 'app/api/competitionApi';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { AUTHORITIES } from 'app/config/constants';
import { getAllLightCompanies } from 'app/api/insuranceCompanyApi';
import { MultiSelect } from 'primereact/multiselect';
import { getProductsByCompanyIds } from 'app/api/productApi';
import { Checkbox } from 'primereact/checkbox';
import DateTimeTextInput from 'app/component/date-time-text-input';

const CreateCompetition: React.FC = () => {
  const { id } = useParams<{ id?: string }>(); // Capture id from URL

  const [searchParamsInitial] = useSearchParams();
  const tempParameters = searchParamsInitial.get('p');

  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const { showSuccess, showError } = useContext(ToastContext);
  const [text, setText] = useState<string>('');
  const editorRef = useRef<Editor>(null);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));

  useEffect(() => {
    getAllLightCompanies()
      .then(response => {
        setCompanies(
          response.data.map(company => ({
            label: company.name,
            value: company.id,
          })),
        );
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      });

    if (id) {
      getCompetition(id)
        .then(response => {
          const d = response.data;

          if (d.companyIds) {
            setSelectedCompanies(d.companyIds.split(',').map(part => Number(part.trim())));
          }
          if (d.productIds) {
            setSelectedProducts(d.productIds.split(',').map(part => Number(part.trim())));
          }

          setData(d);
        })
        .catch(e => {
          console.error(e);
          showError('Chyba', e?.response?.data?.detail);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let companyIds = '';

    if (selectedCompanies?.length > 0 && !data?.allCompanies) {
      companyIds = selectedCompanies.join(',');
    } else if (data?.allCompanies) {
      companyIds = '';
    } else {
      return;
    }

    getProductsByCompanyIds(companyIds)
      .then(response => {
        setProducts(
          response.data.map(company => ({
            label: company.name,
            value: company.id,
          })),
        );
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      });
  }, [selectedCompanies, data?.allCompanies]);

  // Function to upload image to Spring Boot API
  const uploadImage = (file: File) => {
    const formData = new FormData();
    formData.append('image', file);

    axios.post('/api/competitions/upload-image', formData).then(response => {
      const imageUrl = response.data;
      // const imageUrl = data.url;

      const quill = editorRef.current.getQuill();
      const range = quill.getSelection(true);

      const image = 'https://system.nfagroup.eu/api/competitions/image/' + imageUrl;

      quill.insertEmbed(
        range.index,
        'image',
        image,
        // 'https://system.nfagroup.eu/api/competitions/image/' + imageUrl,
        // 'https://www.shutterstock.com/image-vector/bla-doodle-speech-bubble-lettering-600w-2466391955.jpg',
      );
      quill.setSelection(range.index + 1);
    });
  };

  const handleSubmit = (values: any) => {
    setIsSaving(true);

    values = { ...data, companyIds: selectedCompanies?.join(','), productIds: selectedProducts?.join(',') };

    let call;
    if (id) {
      call = updateCompetition(id, values);
    } else {
      call = createCompetition(values);
    }

    call
      .then(response => {
        showSuccess('Úspech', 'Súťaž úspešne uložená.');
        setData(response.data);
      })
      .catch(error => {
        showError('Chyba', error.response?.data?.detail);
        console.error('Error creating agent:', error);
      })
      .finally(() => setIsSaving(false));
  };

  // Image handler for toolbar button
  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = () => {
      const file = input.files?.[0];
      if (file && editorRef.current) {
        uploadImage(file);
        // if (imageUrl) {
        //   const quill = editorRef.current.getQuill();
        //   const range = quill.getSelection(true);
        //   quill.insertEmbed(range.index, 'image', imageUrl);
        //   quill.setSelection(range.index + 1);
        // }
      }
    };
  };

  // Handle paste events to upload images from clipboard
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault();

      const clipboardData = e.clipboardData;
      if (!clipboardData) return;

      const items = clipboardData.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file && editorRef.current) {
            uploadImage(file);
            // if (imageUrl) {
            //   const quill = editorRef.current.getQuill();
            //   const range = quill.getSelection(true);
            //   quill.insertEmbed(range.index, 'image', imageUrl);
            //   quill.setSelection(range.index + 1);
            // }
          }
        } else if (item.type === 'text/plain') {
          item.getAsString(text2 => {
            if (editorRef.current) {
              const quill = editorRef.current.getQuill();
              const range = quill.getSelection(true);
              quill.insertText(range.index, text2);
              quill.setSelection(range.index + text2.length);
            }
          });
        } else if (item.type === 'text/html') {
          item.getAsString(html => {
            const sanitizedHtml = DOMPurify.sanitize(html);
            if (editorRef.current) {
              const quill = editorRef.current.getQuill();
              const range = quill.getSelection(true);
              quill.clipboard.dangerouslyPasteHTML(range.index, sanitizedHtml);
            }
          });
        }
      }
    };

    const quill = editorRef.current?.getQuill();
    if (quill) {
      quill.root.addEventListener('paste', handlePaste);
    }

    return () => {
      if (quill) {
        quill.root.removeEventListener('paste', handlePaste);
      }
    };
  }, []);

  return (
    <div className="card">
      <h3>{id ? (isAdmin ? 'Upraviť súťaž' : 'Informácie o súťaži') : 'Vytvoriť súťaž'}</h3>
      {loading && (
        <div className="flex justify-content-center">
          <ProgressSpinner />
        </div>
      )}
      {!loading && (
        <div>
          <Formik
            initialValues={
              data
                ? data
                : {
                    title: '',
                    data: '',
                  }
            }
            // validationSchema={validationSchema}
            onSubmit={values => {
              handleSubmit(values);
            }}
          >
            {({ isSubmitting, setFieldValue, values, errors }) => {
              return (
                <Form onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} className="p-fluid">
                  <div className="grid">
                    <div className="col-12">
                      <div className="p-field">
                        <label htmlFor="title" className="text-900 font-semibold">
                          Názov súťaže
                        </label>
                        <InputText
                          readOnly={!isAdmin}
                          id={`title`}
                          value={data?.title}
                          onChange={e => setData({ ...data, title: e.target.value })}
                        />
                        <ErrorMessage name="title" component="div" className="p-error" />
                      </div>
                    </div>
                    <div className="col-12 md:col-6">
                      <div className="p-field">
                        <label htmlFor="naZakladePoctuZmluv" className="text-900 font-semibold">
                          Na základe počtu zmluv
                        </label>
                        <Checkbox
                          disabled={!isAdmin}
                          name="naZakladePoctuZmluv"
                          onChange={e => setData({ ...data, naZakladePoctuZmluv: e.checked })}
                          checked={data?.naZakladePoctuZmluv}
                        ></Checkbox>
                      </div>
                    </div>
                    <div className="col-12 md:col-6">
                      <div className="p-field">
                        <label htmlFor="naZakladeRocnehoPoistneho" className="text-900 font-semibold">
                          Na základe ročného poistného
                        </label>
                        <Checkbox
                          disabled={!isAdmin}
                          name="naZakladeRocnehoPoistneho"
                          onChange={e => setData({ ...data, naZakladeRocnehoPoistneho: e.checked })}
                          checked={data?.naZakladeRocnehoPoistneho}
                        ></Checkbox>
                      </div>
                    </div>
                    <div className={`${data?.allCompanies ? 'col-6 md:col-3' : 'col-12 md:col-6'}`}>
                      <label htmlFor="company" className="text-900 font-semibold">
                        Spoločnosti
                      </label>
                      <MultiSelect
                        id="company"
                        disabled={!isAdmin}
                        value={selectedCompanies}
                        options={companies}
                        selectAll={data?.allCompanies}
                        selectAllLabel="Všetky spoločnosti"
                        showSelectAll={true}
                        onSelectAll={e => {
                          setData({ ...data, allCompanies: !e.checked });
                        }}
                        className="custom-multiselect"
                        onChange={e => setSelectedCompanies(e.value)}
                        placeholder="Vyberte spoločnosť"
                        filter
                      />
                    </div>
                    {data?.allCompanies && (
                      <div className="col-6 md:col-3" style={{ marginTop: '30px' }}>
                        <div className="p-field">
                          <label htmlFor="allCompanies">Všetky spoločnosti</label>
                          <Checkbox name="allCompanies" disabled={true} checked={data?.allCompanies}></Checkbox>
                        </div>
                      </div>
                    )}
                    <div className={`${data?.allProducts ? 'col-6 md:col-3' : 'col-12 md:col-6'}`}>
                      <label htmlFor="products" className="text-900 font-semibold">
                        Produkty
                      </label>
                      <MultiSelect
                        id="products"
                        disabled={!isAdmin || (selectedCompanies?.length <= 0 && !data?.allCompanies)}
                        value={selectedProducts}
                        options={products}
                        selectAll={data?.allProducts}
                        selectAllLabel="Všetky produkty"
                        showSelectAll={true}
                        onSelectAll={e => {
                          setData({ ...data, allProducts: !e.checked });
                        }}
                        className="custom-multiselect"
                        onChange={e => setSelectedProducts(e.value)}
                        placeholder="Vyberte produkt"
                        filter
                      />
                    </div>
                    {data?.allProducts && (
                      <div className="col-6 md:col-3" style={{ marginTop: '30px' }}>
                        <div className="p-field">
                          <label htmlFor="allProducts">Všetky produkty</label>
                          <Checkbox name="allProducts" disabled={true} checked={data?.allProducts}></Checkbox>
                        </div>
                      </div>
                    )}

                    <div className="col-12 md:col-6 field">
                      <label htmlFor="validFrom" className="text-900 font-semibold">
                        Platí od
                      </label>
                      <DateTimeTextInput
                        readOnly={!isAdmin}
                        id="validFrom"
                        value={data?.validFrom ? new Date(data.validFrom) : null}
                        onChange={date =>
                          setData(prevState => ({
                            ...prevState,
                            validFrom: date,
                          }))
                        }
                        required
                      />
                    </div>

                    <div className="col-12 md:col-6 field">
                      <label htmlFor="validTo" className="text-900 font-semibold">
                        Platí do
                      </label>
                      <DateTimeTextInput
                        readOnly={!isAdmin}
                        id="validTo"
                        value={data?.validTo ? new Date(data.validTo) : null}
                        onChange={date =>
                          setData(prevState => ({
                            ...prevState,
                            validTo: date,
                          }))
                        }
                        required
                      />
                    </div>

                    <div className="col-12 md:col-6">
                      <div className="p-field">
                        <label htmlFor="berieSaDatumPodpisu" className="text-900 font-semibold">
                          Berie sa dátum podpisu
                        </label>
                        <Checkbox
                          disabled={!isAdmin}
                          name="berieSaDatumPodpisu"
                          onChange={e => setData({ ...data, berieSaDatumPodpisu: e.checked })}
                          checked={data?.berieSaDatumPodpisu}
                        ></Checkbox>
                      </div>
                    </div>
                    <div className="col-12 md:col-6">
                      <div className="p-field">
                        <label htmlFor="berieSaDatumZaciatku" className="text-900 font-semibold">
                          Berie sa dátum začiatku
                        </label>
                        <Checkbox
                          disabled={!isAdmin}
                          name="berieSaDatumZaciatku"
                          onChange={e => setData({ ...data, berieSaDatumZaciatku: e.checked })}
                          checked={data?.berieSaDatumZaciatku}
                        ></Checkbox>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '10px' }}>
                    <Button
                      type="button"
                      label="Späť"
                      icon="pi pi-arrow-left"
                      style={{ backgroundColor: 'gray' }}
                      className="p-button-secondary"
                      onClick={() => navigate(`/competitions?p=${tempParameters}`)}
                    />
                    {isAdmin && (
                      <Button style={{ backgroundColor: '#e31818' }} type="submit" label="Uložiť" icon="pi pi-check" disabled={isSaving} />
                    )}
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      )}
    </div>
  );
};

export default CreateCompetition;
